/**
 * RL-1843: the same rule as the API (RL-1842, lab/result-flag.ts): a result's
 * flag is worked out from its value and reference range. Ranges as the lab
 * writes them ("4.0-5.6", "4 to 5.6", "<5.6", "≥60"); values like "7.8",
 * "<0.5" or ">200". Anything else is not computed (null).
 */
export type ComputedFlag = 'normal' | 'low' | 'high';

// "12,500" is a thousands separator; "5,1" a decimal comma.
const NUM = String.raw`(-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:[.,]\d+)?)`;
const num = (s: string) =>
  /^-?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(s)
    ? Number(s.replace(/,/g, ''))
    : Number(s.replace(',', '.'));

function parseRange(
  range: string,
): { low: number | null; high: number | null } | null {
  const r = range.trim();
  let m = new RegExp(`^${NUM}\\s*(?:-|–|—|to)\\s*${NUM}$`, 'i').exec(r);
  if (m) {
    const [a, b] = [num(m[1]), num(m[2])];
    return a <= b ? { low: a, high: b } : null;
  }
  m = new RegExp(`^(?:<|≤|<=)\\s*${NUM}$`).exec(r);
  if (m) return { low: null, high: num(m[1]) };
  m = new RegExp(`^(?:>|≥|>=)\\s*${NUM}$`).exec(r);
  if (m) return { low: num(m[1]), high: null };
  return null;
}

/** The number and, for an analyser limit ("<0.5", ">200"), its side. */
function parseBound(
  value: string,
): { value: number; side: '<' | '>' | null } | null {
  const m = new RegExp(`^(<|>|≤|≥|<=|>=)?\\s*${NUM}$`).exec(value.trim());
  if (!m) return null;
  const side = !m[1] ? null : m[1].startsWith('<') || m[1] === '≤' ? '<' : '>';
  return { value: num(m[2]), side };
}

export function computeFlag(value: string, range: string): ComputedFlag | null {
  const b = parseBound(value);
  const r = parseRange(range);
  if (!b || !r) return null;
  const v = b.value;
  // An analyser limit is flagged only when its side settles it.
  if (b.side === '<') {
    if (r.low !== null && v <= r.low) return 'low';
    return r.low === null && (r.high === null || v <= r.high) ? 'normal' : null;
  }
  if (b.side === '>') {
    if (r.high !== null && v >= r.high) return 'high';
    return r.high === null && (r.low === null || v >= r.low) ? 'normal' : null;
  }
  if (r.low !== null && v < r.low) return 'low';
  if (r.high !== null && v > r.high) return 'high';
  return 'normal';
}

export const CRITICAL_CONFIRM =
  'This is a critical result. The doctor will be asked to call the patient. Save?';
